import type { UnifiedOrderListItem } from '@flux/shared';
import { CloudDownload, MoreHorizontal, Send, SendHorizontal, XCircle } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { Button } from '@/components/ui/button.js';
import { ConfirmDialog } from '@/components/ui/confirm-dialog.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';
import { RowActionPlaceholder } from '@/features/orders/order-row-actions.js';
import { IgnoreInvoiceDialog } from './ignore-invoice-dialog.js';
import { useForceSendInvoice, useIgnoreInvoice, useRefreshInvoice, useResendInvoice } from './invoice-actions.js';

export interface InvoiceRowActionsProps {
  /** Ligne de la liste unifiée, côté facture Pennylane (`item.invoice` non nul). */
  item: UnifiedOrderListItem;
}

/**
 * Actions de ligne d'une facture Pennylane dans Commandes (`02` §3) :
 * Mettre à jour depuis Pennylane, Renvoyer à Martelet, Forcer l'envoi,
 * Ignorer. Contrairement aux commandes Shopify, une
 * facture n'entre pas dans les actions groupées.
 */
export function InvoiceRowActions({ item }: InvoiceRowActionsProps): JSX.Element {
  const invoice = item.invoice;
  const [confirmResend, setConfirmResend] = useState(false);
  const [ignoreOpen, setIgnoreOpen] = useState(false);
  const refresh = useRefreshInvoice();
  const resend = useResendInvoice();
  const force = useForceSendInvoice();
  const ignore = useIgnoreInvoice();

  if (invoice === null) {
    return <RowActionPlaceholder />;
  }

  const alreadySent = invoice.status === 'sent';

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={`Actions pour la facture ${item.number}`}
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <MoreHorizontal aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={() => {
              refresh.mutate(item.id);
            }}
          >
            <CloudDownload className="size-4" aria-hidden="true" />
            Mettre à jour depuis Pennylane
          </DropdownMenuItem>
          {invoice.status !== 'queued' ? (
            <DropdownMenuItem
              onSelect={() => {
                if (alreadySent) {
                  setConfirmResend(true);
                } else {
                  resend.mutate({ id: item.id, confirmResend: false });
                }
              }}
            >
              <SendHorizontal className="size-4" aria-hidden="true" />
              Renvoyer la facture à Martelet
            </DropdownMenuItem>
          ) : null}
          {invoice.status === 'error' ? (
            <DropdownMenuItem
              onSelect={() => {
                force.mutate(item.id);
              }}
            >
              <Send className="size-4" aria-hidden="true" />
              Forcer l’envoi
            </DropdownMenuItem>
          ) : null}
          {invoice.status !== 'ignored' ? (
            <DropdownMenuItem
              onSelect={() => {
                setIgnoreOpen(true);
              }}
            >
              <XCircle className="size-4" aria-hidden="true" />
              Ignorer
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDialog
        open={confirmResend}
        onOpenChange={setConfirmResend}
        title="Renvoyer la facture à Martelet"
        description={`La facture ${item.number} sera relue dans Pennylane, puis un nouveau fichier sera généré et envoyé. Elle a déjà été transmise à Martelet : si le premier envoi a été intégré, elle peut y être en double.`}
        confirmLabel="Renvoyer"
        isConfirming={resend.isPending}
        onConfirm={() => {
          resend.mutate(
            { id: item.id, confirmResend: true },
            {
              onSuccess: () => {
                setConfirmResend(false);
              },
            },
          );
        }}
      />

      <IgnoreInvoiceDialog
        open={ignoreOpen}
        onOpenChange={setIgnoreOpen}
        description={`La facture ${item.number} ne sera pas transmise à Martelet.`}
        isSubmitting={ignore.isPending}
        onConfirm={(reason) => {
          ignore.mutate(
            { id: item.id, reason },
            {
              onSuccess: () => {
                setIgnoreOpen(false);
              },
            },
          );
        }}
      />
    </>
  );
}
